import type { NavigateFunction } from 'react-router-dom';

/**
 * ============================================================================
 * GUIDED TOUR — CROSS-PAGE NAVIGATION BRIDGE (Phase 3b)
 * ============================================================================
 * Deep-dive sub-tours chain across pages: a sub-tour's final step navigates to
 * the next route, then the destination page auto-fires its own sub-tour.
 *
 * Tour step callbacks (`onNextClick`) live in `tourSteps.ts` — plain objects,
 * not React components — so they cannot call react-router's `useNavigate()`.
 * And a hard `window.location` navigation would wipe the in-memory zustand
 * `pendingPhase` that the handoff depends on.
 *
 * So `TourProvider` (which IS inside the router) registers its `navigate`
 * function here once on mount; tour steps then call `tourNavigate()` to perform
 * a real SPA navigation that preserves the tour store.
 * ============================================================================
 */

let navigateRef: NavigateFunction | null = null;

/** Called once by TourProvider to register the router's navigate function. */
export function setTourNavigate(fn: NavigateFunction): void {
  navigateRef = fn;
}

/**
 * SPA-navigate from inside a tour step callback. Falls back to a hard
 * navigation only if TourProvider hasn't mounted yet (should never happen).
 */
export function tourNavigate(path: string): void {
  if (navigateRef) {
    navigateRef(path);
  } else {
    window.location.assign(path);
  }
}
